/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, Eye, RefreshCcw, ShieldCheck, FileSignature, Lock, XCircle } from "lucide-react";
import { toast } from "sonner";
import { signApi, formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { RentalPeriodNote } from "@/components/location/DateTimeFields";
import { formatDateLong, formatDateTimeLong } from "@/lib/date";

function fmtEur(v) {
  return Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}
function fmtDate(iso) {
  return iso ? (formatDateLong(iso) || "—") : "—";
}

const SignaturePad = React.forwardRef(function SignaturePad({ onChange, disabled }, ref) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = w * ratio;
      canvas.height = h * ratio;
      const ctx = canvas.getContext("2d");
      ctx.scale(ratio, ratio);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2.2;
      ctx.strokeStyle = "#0a0a0a";
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const pointerPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pointerPos(e);
  };
  const move = (e) => {
    if (!drawingRef.current || disabled) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    lastRef.current = { x, y };
    if (!hasInk) {
      setHasInk(true);
      onChange?.(true);
    }
  };
  const end = () => { drawingRef.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange?.(false);
  };

  React.useImperativeHandle(ref, () => ({
    getDataUrl: () => {
      if (!hasInk) return null;
      const src = canvasRef.current;
      const out = document.createElement("canvas");
      out.width = src.width;
      out.height = src.height;
      const ctx = out.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, out.width, out.height);
      ctx.drawImage(src, 0, 0);
      return out.toDataURL("image/png");
    },
    clear,
  }));

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        data-testid="signature-pad-canvas"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className={`block w-full h-44 md:h-52 bg-white border ${disabled ? "border-white/15 opacity-60" : "border-[#DCB457]/40 hover:border-[#DCB457]/70 cursor-crosshair"} transition`}
        style={{ touchAction: "none" }}
      />
      {!hasInk && !disabled && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <p className="text-[#a98e34]/60 text-sm font-serif italic">Tracez ici avec votre doigt ou votre souris</p>
        </div>
      )}
      <button
        type="button"
        onClick={clear}
        disabled={disabled || !hasInk}
        data-testid="signature-pad-clear"
        className="absolute top-2 right-2 text-[10px] uppercase tracking-[0.2em] text-[#a98e34] inline-flex items-center gap-1 bg-white/85 hover:bg-white border border-[#DCB457]/35 px-2 py-1 disabled:opacity-40"
      >
        <RefreshCcw size={10} strokeWidth={1.5} /> Effacer
      </button>
    </div>
  );
});


function RefusedCard({ doc }) {
  return (
    <div className="card-luxe p-6 border-red-400/30" data-testid="signature-refused-card">
      <XCircle size={30} strokeWidth={1.2} className="text-red-300 mb-4" />
      <h2 className="font-serif-luxe text-xl">Devis refusé</h2>
      <p className="text-xs text-white/55 mt-2">le {formatDateTimeLong(doc.refused_at)}</p>
      {doc.refusal_reason && <p className="text-xs text-white/70 mt-4 italic leading-relaxed">« {doc.refusal_reason} »</p>}
      <p className="text-xs text-white/60 mt-5 leading-relaxed">
        Vous avez changé d'avis ou souhaitez une proposition ajustée ? Contactez-nous au 07 69 22 49 56.
      </p>
      <Link to="/contact" className="btn-ghost-gold inline-flex mt-5 !text-[11px]" data-testid="signature-refused-contact">Nous contacter</Link>
    </div>
  );
}

function RefuseModal({ token, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await signApi.refuse(token, { reason: reason.trim() });
      toast.success("Votre refus a bien été transmis. Merci pour votre retour.");
      onDone(await signApi.get(token));
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !busy && onClose()} data-testid="signature-refuse-modal">
      <div className="w-full max-w-md bg-[#0a0a0a] border border-[#DCB457]/30 p-6" onClick={(e) => e.stopPropagation()}>
        <p className="eyebrow">Refuser le devis</p>
        <h3 className="font-serif-luxe text-2xl mt-2">Une raison à nous partager ?</h3>
        <p className="text-xs text-white/55 mt-3 leading-relaxed">Facultatif — votre retour nous aide à améliorer nos propositions (budget, date, prestation…).</p>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} rows={4} className="input-luxe mt-4 w-full" placeholder="Ex. : budget trop élevé, date déjà réservée ailleurs…" data-testid="signature-refuse-reason" />
        <div className="flex gap-3 mt-5">
          <button type="button" onClick={onClose} disabled={busy} className="btn-ghost-gold flex-1 !py-3 !text-[11px]" data-testid="signature-refuse-cancel">Annuler</button>
          <button type="button" onClick={submit} disabled={busy} className="flex-1 py-3 text-[11px] uppercase tracking-[0.18em] border border-red-400/50 text-red-200 hover:bg-red-500/10 transition-colors disabled:opacity-60" data-testid="signature-refuse-confirm">
            {busy ? "Envoi…" : "Confirmer le refus"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SignatureDevis() {
  useSeo({
    title: "Signature de devis en ligne | My Animation",
    description: "Signez votre devis My Animation en toute sécurité.",
    path: "/signature",
    noindex: true,
  });
  const { token } = useParams();
  const [refuseOpen, setRefuseOpen] = useState(() => new URLSearchParams(window.location.search).get("refuser") === "1");
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signatureName, setSignatureName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const padRef = useRef(null);

  useEffect(() => {
    signApi.get(token)
      .then((d) => { setDoc(d); setSignatureName(d.client_name || ""); })
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [token]);

  const onSubmit = async () => {
    if (!accepted) { toast.error("Vous devez accepter les conditions."); return; }
    if (!signatureName.trim() || signatureName.trim().length < 2) {
      toast.error("Veuillez indiquer votre nom complet.");
      return;
    }
    if (!hasInk || !padRef.current?.getDataUrl) {
      toast.error("Veuillez tracer votre signature.");
      return;
    }
    const dataUrl = padRef.current.getDataUrl();
    if (!dataUrl) { toast.error("Signature vide."); return; }
    setSubmitting(true);
    try {
      await signApi.submit(token, {
        signature_data: dataUrl,
        signature_name: signatureName.trim(),
        accept_terms: true,
      });
      toast.success("Devis signé. Un email de confirmation vous est envoyé.");
      const refreshed = await signApi.get(token);
      setDoc(refreshed);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const openPdf = () => window.open(signApi.pdfUrl(token), "_blank");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#DCB457]" />
      </div>
    );
  }
  if (err || !doc) {
    return (
      <div className="min-h-screen bg-[#050505] text-white/85 flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <h2 className="font-serif-luxe text-3xl gold-text mb-4">Lien invalide</h2>
          <p className="text-white/65 mb-6">{err || "Ce lien de signature n'existe pas ou a expiré."}</p>
          <Link to="/contact" className="btn-ghost-gold inline-flex">Nous contacter</Link>
        </div>
      </div>
    );
  }

  const isSigned = !!doc.is_signed;
  const isRefused = doc.status === "refuse";

  return (
    <div className="min-h-screen bg-[#050505] text-white/85" data-testid="signature-page">
      <header className="border-b border-[#DCB457]/15 bg-[#0a0a0a]">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="font-serif-luxe text-2xl italic gold-text">My Animation</Link>
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Signature de devis</p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 md:py-16">
        <p className="eyebrow">Devis · {doc.number}</p>
        <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3 leading-[1.1]">
          {isSigned ? (
            <>Devis <span className="italic gold-text">signé.</span></>
          ) : isRefused ? (
            <>Devis <span className="italic gold-text">refusé.</span></>
          ) : (
            <>Bonjour <span className="italic gold-text">{(doc.client_name || "").split(" ")[0]}.</span></>
          )}
        </h1>
        <p className="text-white/55 mt-4 max-w-2xl">
          {isSigned
            ? `Votre signature a été enregistrée le ${formatDateTimeLong(doc.signed_at)}. Un email de confirmation vous a été envoyé.`
            : isRefused
            ? "Votre réponse a bien été transmise à My Animation. Merci d'avoir pris le temps d'étudier notre proposition."
            : `Voici le récapitulatif de votre devis. Prenez quelques instants pour le relire avant de signer. ${doc.rental_start_at ? "Le contrat de location de matériel" : "Le contrat de prestation"} et nos coordonnées bancaires sont inclus dans le PDF.`}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
          <div className="lg:col-span-2 space-y-5">
            <section className="card-luxe p-6">
              <p className="eyebrow">Prestation</p>
              <p className="text-xl font-serif-luxe mt-2">
                {doc.event_type}
                {doc.event_date && <span className="text-white/55"> · le {fmtDate(doc.event_date)}</span>}
                {doc.event_location && <span className="text-white/55"> · à {doc.event_location}</span>}
              </p>
              {doc.rental_start_at && <RentalPeriodNote rental={{ start: doc.rental_start_at, end: doc.rental_end_at, mode: doc.rental_mode, number: doc.rental_number }} publicView />}

              <ul className="mt-5 divide-y divide-[#DCB457]/10">
                {(doc.lines || []).map((l, i) => (
                  <li key={i} className="py-3 flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm text-white/85">{l.description}</p>
                      {l.notes && <p className="text-xs text-white/45 mt-1 italic">{l.notes}</p>}
                    </div>
                    <p className="text-sm text-white/85 whitespace-nowrap">
                      <span className="text-white/40">{l.unit === "heure" ? `${l.quantity} h × ${fmtEur(l.unit_price)} = ` : `${l.quantity}× `}</span>
                      {fmtEur(l.quantity * l.unit_price)}
                    </p>
                  </li>
                ))}
              </ul>

              <div className="mt-6 pt-5 border-t border-[#DCB457]/15 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-white/55">Total TTC</span>
                  <span className="font-serif-luxe text-2xl gold-text">{fmtEur(doc.total_ttc)}</span>
                </div>
                <div className="flex justify-between text-xs text-white/55">
                  <span>Acompte ({doc.deposit_pct}%) — à la signature</span>
                  <span className="text-white/85">{fmtEur(doc.deposit_ttc)}</span>
                </div>
                <div className="flex justify-between text-xs text-white/55">
                  <span>Solde — au plus tard 4 jours avant</span>
                  <span className="text-white/85">{fmtEur((doc.total_ttc || 0) - (doc.deposit_ttc || 0))}</span>
                </div>
              </div>

              {doc.valid_until && (
                <p className="text-xs text-white/45 mt-5 italic">
                  Devis valable jusqu'au {fmtDate(doc.valid_until)}
                </p>
              )}
            </section>

            <button onClick={openPdf} className="btn-ghost-gold inline-flex items-center gap-2" data-testid="signature-view-pdf">
              <Eye size={14} strokeWidth={1.4} /> Voir le contrat & RIB complets (PDF)
            </button>
          </div>

          <aside className="space-y-5">
            {isRefused ? (
              <RefusedCard doc={doc} />
            ) : isSigned ? (
              <div className="card-luxe p-6 border-emerald-500/40">
                <CheckCircle2 size={32} strokeWidth={1.2} className="text-emerald-300 mb-4" />
                <h2 className="font-serif-luxe text-xl">Signature enregistrée</h2>
                <p className="text-xs text-white/55 mt-2">
                  Signé par <span className="text-white/85">{doc.signature_name}</span><br />
                  le {formatDateTimeLong(doc.signed_at)}
                </p>
                <div className="mt-5 p-4 bg-[#DCB457]/8 border border-[#DCB457]/30">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#DCB457] mb-2">Étape suivante</p>
                  <p className="text-xs text-white/85 leading-relaxed">
                    Pour réserver définitivement votre date, effectuez le virement bancaire de l'acompte
                    (<strong>{fmtEur(doc.deposit_ttc)}</strong>) en utilisant les coordonnées dans le PDF.
                    Référence : <span className="font-mono">{doc.number}</span>.
                  </p>
                </div>
              </div>
            ) : (
              <div className="card-luxe p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FileSignature size={18} strokeWidth={1.4} className="text-[#DCB457]" />
                  <h2 className="font-serif-luxe text-xl">Signer le devis</h2>
                </div>

                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-white/55">Votre nom complet</span>
                  <input
                    type="text"
                    className="input-luxe mt-2"
                    value={signatureName}
                    onChange={(e) => setSignatureName(e.target.value)}
                    data-testid="signature-name-input"
                  />
                </label>

                <div className="mt-5">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 mb-2">Signature manuscrite</p>
                  <SignaturePad ref={padRef} onChange={setHasInk} disabled={submitting} />
                </div>

                <label className="mt-5 flex items-start gap-2.5 text-xs text-white/65 leading-relaxed cursor-pointer">
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    data-testid="signature-accept-terms"
                    className="mt-0.5 accent-[#DCB457]"
                  />
                  <span>
                    Je déclare avoir lu et accepté le devis ainsi que le {doc.rental_start_at ? "contrat de location de matériel" : "contrat de prestation"} et les conditions associées, mentionnés ci-joint en PDF.
                  </span>
                </label>

                <button
                  onClick={onSubmit}
                  disabled={submitting}
                  data-testid="signature-submit-btn"
                  className="btn-gold inline-flex items-center justify-center gap-2 w-full mt-5 disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} strokeWidth={1.5} />}
                  Signer définitivement
                </button>

                <div className="mt-5 pt-5 border-t border-[#DCB457]/15 flex items-start gap-2 text-[10px] uppercase tracking-[0.18em] text-white/40">
                  <Lock size={11} strokeWidth={1.4} className="mt-0.5" />
                  <span>Signature électronique horodatée — valeur légale équivalente à une signature manuscrite (eIDAS).</span>
                </div>
                <button type="button" onClick={() => setRefuseOpen(true)} data-testid="signature-refuse-open" className="mt-5 w-full text-[11px] uppercase tracking-[0.18em] text-white/45 hover:text-red-300 underline underline-offset-4 transition-colors">
                  Refuser ce devis
                </button>
              </div>
            )}
          </aside>
        </div>
      </main>

      {refuseOpen && !isSigned && !isRefused && (
        <RefuseModal token={token} onClose={() => setRefuseOpen(false)} onDone={setDoc} />
      )}

      <footer className="border-t border-[#DCB457]/10 mt-20 py-8 text-center text-[11px] text-white/40">
        <p>My Animation · DJ événementiel · 07 69 22 49 56 · my-animation@outlook.fr</p>
      </footer>
    </div>
  );
}
